// 📰 News (Phase B2) — the staff feed + per-person PIN confirm (index.html tab).
//
// ToyTalk "Read" replacement, reading surface. Product rules:
//   - The feed shows posts targeted at THIS store (or all-stores posts),
//     pinned first, unconfirmed highlighted with a NEW chip.
//   - CONFIRMING is per PERSON and requires the PIN (memory only, 5-min idle —
//     the chat A3 posture; the Worker attributes the confirm from the VERIFIED
//     staff row). The who-record is SHARED with chat ('chat_who_v1') so one
//     pick covers both tabs.
//   - Accounts (AM / HO / ho_staff) logged into the staff app read the feed on
//     their Bearer (A6-4 account mode) — no confirm button; confirms are the
//     store-staff compliance signal, not a manager checkbox.
//   - Images ride on demand (GET /api/news/photo) so the feed stays light.
//
// Bare-name globals from index.html (currentScreen, currentStore, currentUser,
// getStoreStaff, DB) are typeof-guarded — the classic-script bundle contract.

// v129.113 — this file had NO imports until now, which is exactly how the
// v129.112 near-miss happened (an import that fails to apply leaves an undefined
// global, and a source-scanning test cannot see it). The test for this asserts
// the BUILT dist/news.js carries the shared code.
import { attachLinksHtml, openFromPayload } from '../shared/attachments-ui.js';

function esc(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
// SECURITY (v125.14): news images are HO-posted (trusted source), but blank any
// non-base64-image src before innerHTML anyway — same guard as chat, one rule
// everywhere. validatePhoto on the server is the primary fix.
function okPhoto(s) {
  return /^data:image\/(jpeg|jpg|png|webp);base64,[A-Za-z0-9+/]+={0,2}$/.test(String(s == null ? '' : s)) ? String(s) : '';
}
function initials(name) {
  var p = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!p.length) return '?';
  return (p[0][0] + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase();
}
function fmtWhen(iso) {
  try {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) + ' ' +
      String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
  } catch (e) { return ''; }
}

function api(path, opts) {
  opts = opts || {};
  var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  var headers = { 'Content-Type': 'application/json' };
  var tk = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  if (tk) headers['Authorization'] = 'Bearer ' + tk;
  return fetch(base + path, { method: opts.method || 'GET', headers: headers, body: opts.body, cache: 'no-store' });
}

function isAccountMode() {
  try {
    return !!(typeof currentUser !== 'undefined' && currentUser &&
      ['admin', 'area_manager', 'ho_staff'].indexOf(String(currentUser.role)) !== -1 &&
      typeof DB !== 'undefined' && DB.getToken && DB.getToken());
  } catch (e) { return false; }
}

// ── State (the PIN is deliberately NOT persisted anywhere) ────────────────────
var _who = null;                 // { id, name } — SHARED key 'chat_who_v1' (one pick, both tabs)
var _pin = null;                 // in-memory only
var _pinAt = 0;
var PIN_IDLE_MS = 5 * 60 * 1000;
// 🔗 The notice board (v125.2) — the useful links ToyTalk kept on its dashboard.
var NOTICE_LINKS = [
  // v128.3: 📖 Policies opens the IN-APP reader (the mirrored JACK library) —
  // the old SharePoint folder link is superseded; each policy's original
  // document is still linked from its detail view (file_url).
  { label: '📖 Policies', screen: 'policies' },
  { label: '🎓 EnterTrainer', url: 'https://theentertrainer.learningpool.com/login/index.php' },
  { label: '🧸 elctoys.com', url: 'https://elctoys.com/' },
  { label: '🎫 Tazkera', url: 'https://tickets.algt-me.com/' },
  { label: '🤖 Ask Jack', url: 'https://elcapp-jack.netlify.app/' },
];
var _posts = [];
var _confirming = false;
var _imgCache = {};              // post id → [data URLs] (up to 3, D1)
var _comments = {};              // post id → { open, list } (D1 comments)
var _q = '';                     // v126.65: staff search across title + body
var _scope = 'all';              // v126.65: 'all' | 'me' (me = not yet confirmed by me)
var _embedded = false;           // v126.66: inside the Updates hub → hide our own bar (the hub shows one shared)

function loadWho() {
  if (_who) return _who;
  try {
    var raw = localStorage.getItem('chat_who_v1');
    if (raw) { var w = JSON.parse(raw); if (w && w.id && w.name) _who = { id: w.id, name: w.name }; }
  } catch (e) { /* fresh pick */ }
  return _who;
}
function saveWho(w) {
  _who = w ? { id: w.id, name: w.name } : null;  // id + name ONLY — never a PIN
  try {
    if (_who) localStorage.setItem('chat_who_v1', JSON.stringify(_who));
    else localStorage.removeItem('chat_who_v1');
  } catch (e) { /* storage unavailable — session-only */ }
}
function pinFresh() { return _pin && (Date.now() - _pinAt) < PIN_IDLE_MS; }
function clearPin() { _pin = null; _pinAt = 0; }

function root() { return document.getElementById('newsRoot'); }
function onNewsScreen() {
  // v126.66: 'updates' is the hub that hosts the news root — treat it as on-screen
  // so background refreshes still repaint the feed.
  return (typeof currentScreen === 'undefined' || currentScreen === 'news' || currentScreen === 'updates') && !document.hidden;
}

// ── Styles (Jack-adjacent, own nw- namespace) ─────────────────────────────────
var _styled = false;
function ensureStyles() {
  if (_styled) return;
  _styled = true;
  var css = '' +
    '#newsRoot .nw-shell{position:relative;max-width:680px;margin:0 auto;background:var(--bg,#f0f2f5);border-radius:12px;overflow:hidden;display:flex;flex-direction:column;min-height:60vh}' +
    '#newsRoot .nw-head{background:var(--brand,#009E3D);color:#fff;padding:12px 16px;display:flex;align-items:center;gap:10px}' +
    '#newsRoot .nw-hname{font-weight:700;font-size:16px}' +
    '#newsRoot .nw-hsub{font-size:11.5px;opacity:.85}' +
    '#newsRoot .nw-feed{padding:12px;display:flex;flex-direction:column;gap:10px}' +
    '#newsRoot .nw-card{background:var(--card);border-radius:10px;padding:12px 14px;box-shadow:0 1px 1px rgba(0,0,0,.08)}' +
    '#newsRoot .nw-card.nw-unread{border-inline-start:4px solid var(--brand,#009E3D)}' +   // v135.1: logical side, mirrors for Arabic
    '#newsRoot .nw-title{font-weight:700;font-size:15px;color:var(--text);display:flex;align-items:center;gap:6px;flex-wrap:wrap}' +
    '#newsRoot .nw-chip{font-size:10px;font-weight:800;background:var(--brand,#009E3D);color:#fff;border-radius:8px;padding:1px 7px}' +
    '#newsRoot .nw-pin{font-size:10px;font-weight:800;background:#ffd54f;color:#5d4a00;border-radius:8px;padding:1px 7px}' +
    '#newsRoot .nw-body{font-size:13.5px;color:var(--comms-body);margin-top:6px;white-space:pre-wrap;word-break:break-word}' +
    '#newsRoot .nw-img{margin-top:8px;max-width:100%;border-radius:8px;cursor:pointer;display:block}' +
    '#newsRoot .nw-meta{font-size:11px;color:var(--comms-mute);margin-top:8px}' +
    '#newsRoot .nw-confirm{margin-top:10px;background:var(--brand,#009E3D);color:#fff;border:none;border-radius:8px;padding:8px 14px;font-weight:700;font-size:13px;cursor:pointer}' +
    '#newsRoot .nw-done{margin-top:10px;color:var(--comms-brand-ink);font-weight:700;font-size:13px}' +
    '#newsRoot .nw-empty{padding:28px;text-align:center;color:var(--comms-sub);font-size:13.5px}' +
    '#newsRoot .nw-notice{background:var(--comms-note-bg);color:var(--comms-note-ink);font-size:12px;padding:8px 12px;text-align:center}' +
    '#newsRoot .nw-pick{background:var(--card);max-height:50vh;overflow-y:auto}' +
    '#newsRoot .nw-item{display:flex;gap:10px;padding:10px 14px;border-bottom:1px solid var(--surface-3);cursor:pointer;align-items:center}' +
    '#newsRoot .nw-iav{width:36px;height:36px;border-radius:50%;background:var(--brand,#009E3D);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;flex:0 0 36px}' +
    '#newsRoot .nw-modal{position:fixed;inset:0;background:rgba(11,20,26,.6);display:flex;align-items:center;justify-content:center;z-index:3000}' +
    '#newsRoot .nw-mcard{background:var(--card);border-radius:12px;padding:20px;width:min(340px,90vw);text-align:center}' +
    '#newsRoot .nw-mpin{width:100%;font-size:22px;letter-spacing:8px;text-align:center;padding:10px;border:2px solid var(--text);border-radius:10px;margin:14px 0}' +
    '#newsRoot .nw-mbtn{width:100%;background:var(--brand,#009E3D);color:#fff;border:none;border-radius:10px;padding:11px;font-weight:700;font-size:14px;cursor:pointer}' +
    '#newsRoot .nw-mcancel{margin-top:10px;color:var(--comms-sub);background:none;border:none;font-size:13px;cursor:pointer}';
  var el = document.createElement('style');
  el.id = 'newsStyles';
  el.textContent = css;
  document.head.appendChild(el);
}

// ── Screen entry point (called by switchScreen('news')) ──────────────────────
// v125.51 (Matt: "i dont want [news] hidden behind a pin — pin should only come
// after confirming"): the feed renders IMMEDIATELY, keyed by the signed-in store
// when nobody has picked a name yet. The who-picker now appears only on demand —
// confirming / commenting — and the PIN only after that.
function renderNewsScreen() {
  ensureStyles();
  loadWho();          // restore a previous pick if there is one (personal state)
  renderFeed();
  refreshFeed();
}

// The store this device is signed into (bare-name global from index.html).
function viewerStore() {
  try {
    if (typeof currentStore !== 'undefined' && currentStore) return currentStore.name || currentStore;
  } catch (e) { /* none */ }
  return '';
}

// One action waiting on a who-pick (e.g. tapped Confirm before picking a name).
var _pendingAction = null;

// Same picker contract as chat — writes the SHARED who record.
function renderWhoPicker() {
  var el = root(); if (!el) return;
  var staff = [];
  // v127.42 (a store manager's report #2, task-close picker 29 Jul): the identity picker shows
  // home staff + TODAY'S ROSTERED COVER (getStoreRoster's v113.0 second arg),
  // so a supporter transferred to another store can act under their own name
  // at the store they're covering — the same rule as clock/checks/tally.
  // Falls back to home-only where the roster helper isn't loaded (chat.html).
  try {
    if (typeof currentStore !== 'undefined') {
      var _rosterDay = (typeof businessDayStr === 'function') ? businessDayStr() : null;
      if (typeof getStoreRoster === 'function') staff = getStoreRoster(currentStore, _rosterDay) || [];
      else if (typeof getStoreStaff !== 'undefined') staff = getStoreStaff(currentStore) || [];
    }
  } catch (e) { staff = []; }
  var rows = staff.map(function (s) {
    return '<div class="nw-item" onclick="News.pickWho(' + s.id + ',\'' + esc(String(s.name || '').replace(/'/g, '')) + '\')">' +
      '<div class="nw-iav" translate="no">' + esc(initials(s.name)) + '</div>' +
      '<div><div style="font-weight:600;font-size:14px;color:var(--text)" translate="no">' + esc(s.name) + '</div>' +
      '<div style="font-size:12px;color:var(--comms-mute)">' + esc(s.role || '') + '</div></div></div>';
  }).join('');
  el.innerHTML = '<div class="nw-shell">' +
    '<div class="nw-head"><div style="font-size:22px">📰</div><div><div class="nw-hname">News</div><div class="nw-hsub">Who are you?</div></div></div>' +
    '<div class="nw-notice">Pick your name so this action is recorded as you. You\'ll only need your PIN to confirm.</div>' +
    '<div class="nw-pick">' + (rows || '<div class="nw-empty">No staff found for this store yet.</div>') + '</div>' +
    '<div style="padding:12px;text-align:center;font-size:13px;font-weight:700;color:var(--comms-sub);cursor:pointer" onclick="News.cancelWhoPick()">‹ Back to news</div></div>';
}
// v125.51: the picker is on-demand now — backing out must return to the feed.
function cancelWhoPick() {
  _pendingAction = null;
  renderNewsScreen();
}
function pickWho(id, name) {
  saveWho({ id: id, name: name });
  clearPin();
  renderNewsScreen();
  // Resume the action that opened the picker (v125.51: confirm/comment-on-demand).
  if (_pendingAction) { var a = _pendingAction; _pendingAction = null; a(); }
}

// ── The feed ──────────────────────────────────────────────────────────────────
async function refreshFeed() {
  var q;
  if (isAccountMode()) q = '/api/news';
  else if (_who) q = '/api/news?staff_id=' + encodeURIComponent(_who.id);
  else {
    // v125.51: read-before-pick — the feed is keyed by the signed-in store.
    var st = viewerStore();
    if (!st) return;
    q = '/api/news?store=' + encodeURIComponent(st);
  }
  try {
    var res = await api(q);
    if (!res.ok) return;
    var d = await res.json();
    if (d && d.ok) {
      _posts = d.posts || [];
      updateNavBadge();
      if (onNewsScreen()) renderFeed();
    }
  } catch (e) { /* poll again later */ }
}

function updateNavBadge() {
  var unread = 0;
  // Only badge when we know WHO is reading (v125.51) — a store-keyed feed can't
  // know what this person has confirmed, so a count would nag falsely.
  if (!isAccountMode() && _who) _posts.forEach(function (p) { if (!p.confirmed) unread++; });
  var nav = document.getElementById('nav-news');
  if (!nav) return;
  nav.classList.toggle('nav-attn', unread > 0);
  var b = document.getElementById('newsNavBadge');
  if (unread > 0) {
    if (!b) {
      b = document.createElement('span');
      b.id = 'newsNavBadge';
      b.style.cssText = 'position:absolute;top:2px;inset-inline-end:14px;min-width:16px;height:16px;border-radius:8px;background:var(--brand,#009E3D);color:#fff;font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center;padding:0 4px';
      nav.style.position = 'relative';
      nav.appendChild(b);
    }
    b.textContent = unread > 99 ? '99+' : String(unread);
  } else if (b) b.remove();
}

// v126.65 (Matt: "option (all & me). so we can search easy our task and news") —
// a compact filter bar on the feed: 🔍 search across title + body, and (for a
// staff member who has picked a name) an All | Me toggle where "Me" = not yet
// confirmed by me. Also driven from the Updates hub (PR2) via setFilter().
function matchesFilter(p) {
  // "Me" is unconfirmed-by-me — only knowable for a named staff reader; accounts
  // have no personal confirm, so Me never hides anything for them.
  if (_scope === 'me' && !isAccountMode() && _who && p.confirmed) return false;
  var q = _q.trim().toLowerCase();
  if (!q) return true;
  return ((String(p.title || '') + ' ' + String(p.body || '')).toLowerCase().indexOf(q) !== -1);
}
function filterBarHtml() {
  if (_embedded) return '';       // v126.66: the Updates hub renders the one shared bar
  var showScope = !isAccountMode() && !!_who;   // All|Me needs a picked name (per-person confirms)
  var seg = function (val, label) {
    var on = _scope === val;
    return '<span onclick="News.setScope(\'' + val + '\')" style="cursor:pointer;padding:5px 13px;border-radius:14px;font-size:12px;font-weight:700;' +
      (on ? 'background:var(--brand,#009E3D);color:#fff' : 'background:var(--surface-3);color:var(--comms-sub)') + '">' + label + '</span>';
  };
  return '<div style="display:flex;gap:8px;align-items:center;margin-bottom:10px;flex-wrap:wrap">' +
    '<input id="nwSearch" value="' + esc(_q) + '" oninput="News.onSearch(this.value)" placeholder="🔍 Search news…" ' +
    'style="flex:1;min-width:150px;padding:8px 12px;border:1px solid var(--border-strong);border-radius:10px;font-size:13px;box-sizing:border-box">' +
    (showScope ? '<div style="display:flex;gap:6px">' + seg('all', 'All') + seg('me', 'Me') + '</div>' : '') + '</div>';
}
function onSearch(v) {
  _q = String(v == null ? '' : v);
  renderFeed();
  var i = document.getElementById('nwSearch');
  if (i) { try { i.focus(); i.setSelectionRange(i.value.length, i.value.length); } catch (e) {} }
}
function setScope(s) { _scope = (s === 'me') ? 'me' : 'all'; renderFeed(); }
function setFilter(f) {
  if (f) { if (f.q != null) _q = String(f.q); if (f.scope != null) _scope = (f.scope === 'me') ? 'me' : 'all'; }
  renderFeed();
}
// v126.66 — the Updates hub host: hide our own bar, and expose the unread count
// for the hub's combined nav badge (unconfirmed posts, per-person, when a name
// is picked — mirrors updateNavBadge).
function setEmbedded(on) { _embedded = !!on; }
function unreadCount() {
  var n = 0;
  if (!isAccountMode() && _who) _posts.forEach(function (p) { if (!p.confirmed) n++; });
  return n;
}

function renderFeed() {
  var el = root(); if (!el) return;
  var acct = isAccountMode();
  var searching = _q.trim() !== '';
  var cards = _posts.filter(matchesFilter).map(function (p) {
    // NEW chips + unread highlights only when a name is picked (v125.51) — the
    // store-keyed feed doesn't know this person's confirms.
    var chips = (p.pinned ? '<span class="nw-pin">📌 PINNED</span>' : '') +
      (!acct && _who && !p.confirmed ? '<span class="nw-chip">NEW</span>' : '');
    var img = p.has_image
      ? (_imgCache[p.id]
        ? _imgCache[p.id].map(function (src, i) { return '<img class="nw-img" src="' + src + '" onclick="News.viewImage(' + p.id + ',' + i + ')" alt="">'; }).join('')
        : '<div id="nwImg' + p.id + '" class="nw-meta">📷 Loading image…</div>')
      : '';
    // v125.84 — the post's reference file (PDF/Word/Excel — v158), tasks-style.
    // v129.113 — a post can carry SEVERAL files now; one link each.
    var attach = attachLinksHtml(p, 'News.openAttachment', { className: 'nw-meta', prefix: 'View ' });
    var action = acct ? ''
      : (p.confirmed
        ? '<div class="nw-done">✓ Confirmed — thanks!</div>'
        : '<button class="nw-confirm" onclick="News.confirmPost(' + p.id + ')">✓ Confirm I\'ve read this</button>');
    var cState = _comments[p.id] || {};
    var comments = !p.allow_comments ? '' :
      '<div style="margin-top:8px"><span style="font-size:12.5px;color:var(--comms-brand-ink);font-weight:700;cursor:pointer" onclick="News.toggleComments(' + p.id + ')">💬 Comments' + (cState.list ? ' (' + cState.list.length + ')' : '') + '</span>' +
      (cState.open
        ? '<div id="nwCm' + p.id + '" style="margin-top:6px">' + renderComments(p.id) + '</div>'
        : '') + '</div>';
    return '<div class="nw-card' + (!acct && _who && !p.confirmed ? ' nw-unread' : '') + '">' +
      // v135.1: the post's title, body and author are what Head Office wrote: never translated.
      '<div class="nw-title">' + chips + '<span translate="no">' + esc(p.title) + '</span></div>' +
      '<div class="nw-body" translate="no">' + ((typeof RichText !== 'undefined' && RichText.formatRich) ? RichText.formatRich(p.body, { breaks: false }) : esc(p.body)) + '</div>' + img + attach +
      '<div class="nw-meta">' + (p.created_by ? '<span translate="no">' + esc(p.created_by) + '</span>' : 'Head Office') + ' · ' + esc(fmtWhen(p.created_at)) + '</div>' +
      comments + action + '</div>';
  }).join('');
  el.innerHTML = '<div class="nw-shell">' +
    '<div class="nw-head"><div style="font-size:22px">📰</div><div><div class="nw-hname">News</div>' +
    '<div class="nw-hsub">' + (acct ? 'Company news' : '🔒 Confirmations are visible to Head Office') + '</div></div>' +
    (!acct ? '<div style="margin-inline-start:auto;font-size:11.5px;background:rgba(255,255,255,.15);border-radius:12px;padding:4px 10px;cursor:pointer" onclick="News.switchWho()">' + (_who ? '<span translate="no">' + esc(_who.name) + '</span> · switch' : 'Choose your name') + '</div>' : '') +
    '</div>' +
    // While searching, drop the notice-board + email chrome so the results are
    // clean (they reappear the moment the search box is cleared).
    '<div class="nw-feed">' + filterBarHtml() + (searching ? '' : ('<div class="nw-card"><div class="nw-title">🔗 Notice board</div><div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:8px">' + NOTICE_LINKS.map(function(l){
      var style = 'background:var(--brand,#009E3D);color:#fff;text-decoration:none;font-size:12.5px;font-weight:700;padding:8px 14px;border-radius:8px';
      // v128.3: screen entries switch INSIDE the app (📖 Policies) instead of opening a URL.
      if (l.screen) return '<a href="#" onclick="if(typeof switchScreen===\'function\')switchScreen(\'' + l.screen + '\');return false" style="' + style + '">' + esc(l.label) + '</a>';
      return '<a href="' + l.url + '" target="_blank" rel="noopener" style="' + style + '">' + esc(l.label) + '</a>';
    }).join('') + '</div></div>' +
    // v125.39 (Matt): the card is a POINTER now — store email LIVES in Comms →
    // 📧 Email (v125.36-38), not here. v125.50 (Matt: "i clicked on it and now i
    // cant out of it as it is a blocked feature"): the card renders NON-tappable;
    // fetchEmailPreview() only wires the tap through when the inbox API answers,
    // so pre-go-live nobody can be led into the "coming soon" dead end.
    // v125.90 — Store Email is LIVE (first confirmed delivery 2026-07-16): the
    // default copy is the live pointer; fetchEmailPreview still enriches it with
    // the unread count when the API answers.
    '<div class="nw-card" id="nwEmailCard"><div class="nw-title">📧 Store Email <span id="nwEmailBadge"></span></div><div style="font-size:12.5px;color:var(--sub);margin-top:6px" id="nwEmailSub">Your store\'s inbox lives in Comms → 📧 Email.</div></div>')) +
    (cards || '<div class="nw-empty">' + (searching
      ? 'No news matches "' + esc(_q.trim()) + '".'
      : (_scope === 'me' ? 'All caught up — nothing new to read. 🎉' : 'No news yet — new posts from Head Office show here.')) + '</div>') + '</div></div>';
  fetchImages();
  fetchEmailPreview();
}

// v125.51: the feed no longer gates on a pick, so switching must open the
// picker explicitly (renderNewsScreen would just show the feed again).
function switchWho() { saveWho(null); clearPin(); renderWhoPicker(); }

async function fetchImages() {
  for (var i = 0; i < _posts.length; i++) {
    var p = _posts[i];
    if (!p.has_image || _imgCache[p.id]) continue;
    try {
      // v125.81 (Matt: "in news - stores can not see attachments") — on a till
      // browsing WITHOUT a picked name (_who null, the v125.51 read-before-pick
      // state), `_who.id` threw and the catch silently skipped EVERY image.
      // Fall back to the validated ?store= identity, same as tasks.
      var q = '';
      if (!isAccountMode()) {
        if (_who) { q = '&staff_id=' + encodeURIComponent(_who.id); }
        else {
          var st = (typeof currentStore !== 'undefined' && currentStore) ||
            (typeof window !== 'undefined' && window.currentStore) || '';
          if (st) q = '&store=' + encodeURIComponent(st);
        }
      }
      var res = await api('/api/news/photo?id=' + p.id + q);
      if (!res.ok) continue;
      var d = await res.json();
      var list = (d && Array.isArray(d.photos)) ? d.photos : (d && d.photo ? [d.photo] : []);
      if (list.length) {
        _imgCache[p.id] = list;
        var slot = document.getElementById('nwImg' + p.id);
        if (slot) slot.outerHTML = list.map(function (src, ix) {
          return '<img class="nw-img" src="' + src + '" onclick="News.viewImage(' + p.id + ',' + ix + ')" alt="">';
        }).join('');
      }
    } catch (e) { /* images are optional */ }
  }
}

function viewImage(id, ix) {
  var list = _imgCache[id]; if (!list || !list.length) return;
  var src = list[ix || 0] || list[0];
  var el = root(); if (!el) return;
  var wrap = document.createElement('div');
  wrap.className = 'nw-modal';
  wrap.onclick = function () { wrap.remove(); };
  wrap.innerHTML = '<img src="' + okPhoto(src) + '" style="max-width:92vw;max-height:88vh;border-radius:10px" alt="">';
  el.appendChild(wrap);
}

// ── v125.84 — the post's reference file (PDF/Word/Excel — v158), tasks-style ──
// Fetched on demand with the same read identity as the feed (staff_id / store /
// Bearer — the fetchImages fallback chain).
function newsReadId() {
  if (isAccountMode()) return '';
  if (_who) return '&staff_id=' + encodeURIComponent(_who.id);
  var st = (typeof currentStore !== 'undefined' && currentStore) ||
    (typeof window !== 'undefined' && window.currentStore) || '';
  return st ? '&store=' + encodeURIComponent(st) : '';
}
// v129.113 — `idx` picks which of the post's files to open.
async function openAttachment(postId, idx) {
  try {
    var res = await api('/api/news/attachment?id=' + encodeURIComponent(postId) + newsReadId());
    if (!res.ok) { if (typeof showToast !== 'undefined') showToast('Could not open the attachment', 'error'); return; }
    var d = await res.json();
    if ((!d || !d.ok || !openFromPayload(d, Number(idx) || 0)) && typeof showToast !== 'undefined') {
      showToast('Could not open the attachment', 'error');
    }
  } catch (e) { if (typeof showToast !== 'undefined') showToast('Could not open the attachment', 'error'); }
}
// v129.113 — this was a THIRD correct-but-duplicated copy of the v125.81 blob
// opener. v129.112 made one shared opener because a broken copy in the admin app
// was silently swallowing Head Office's PDF; leaving a duplicate in a file being edited
// for the very same feature is how a fourth copy appears. openFromPayload above
// calls the shared one. (The two chat copies are untouched — a separate tidy.)

// ── Comments (D1 — per-post opt-in; PIN-attributed for staff) ─────────────────
function renderComments(id) {
  var st = _comments[id] || {};
  var list = st.list || [];
  var rows = list.map(function (c) {
    return '<div style="font-size:12.5px;margin-bottom:4px" translate="no"><b>' + esc(c.name) + '</b>' +
      (c.store ? ' <span style="color:var(--comms-mute)">(' + esc(c.store) + ')</span>' : '') +
      ' ' + esc(c.body) + '</div>';
  }).join('');
  return (rows || '<div style="font-size:12px;color:var(--comms-mute)">No comments yet — be the first!</div>') +
    '<div style="display:flex;gap:6px;margin-top:6px">' +
    '<input id="nwCmIn' + id + '" maxlength="500" placeholder="Add a comment…" style="flex:1;padding:7px 9px;border:1px solid var(--border-strong);border-radius:8px;font-size:12.5px">' +
    '<button class="nw-confirm" style="margin-top:0;padding:7px 12px" onclick="News.addComment(' + id + ')">Send</button></div>';
}

async function toggleComments(id) {
  var st = _comments[id] = _comments[id] || {};
  st.open = !st.open;
  if (st.open) {
    try {
      // v125.81 — same read-before-pick crash as fetchImages: fall back to ?store=.
      var q = '';
      if (!isAccountMode()) {
        if (_who) { q = '&staff_id=' + encodeURIComponent(_who.id); }
        else {
          var stName = (typeof currentStore !== 'undefined' && currentStore) ||
            (typeof window !== 'undefined' && window.currentStore) || '';
          if (stName) q = '&store=' + encodeURIComponent(stName);
        }
      }
      var res = await api('/api/news/comments?id=' + id + q);
      var d = await res.json().catch(function () { return {}; });
      if (res.ok && d && d.ok) st.list = d.comments || [];
    } catch (e) { /* show what we have */ }
  }
  renderFeed();
}

function addComment(id) {
  var input = document.getElementById('nwCmIn' + id);
  var text = input ? String(input.value || '').trim() : '';
  if (!text) return;
  if (isAccountMode()) { doAddComment(id, text); return; }
  // v125.51: identity on demand — carry the typed text through the who-pick.
  if (!_who) { _pendingAction = function () { askPin(function () { doAddComment(id, text); }); }; renderWhoPicker(); return; }
  if (pinFresh()) { doAddComment(id, text); return; }
  askPin(function () { doAddComment(id, text); });
}

async function doAddComment(id, text) {
  try {
    var payload = isAccountMode() ? { post_id: id, body: text } : { post_id: id, body: text, auth_staff_id: _who.id, auth_pin: _pin };
    var res = await api('/api/news/comment', { method: 'POST', body: JSON.stringify(payload) });
    var d = await res.json().catch(function () { return {}; });
    if (res.ok && d && d.ok) {
      _pinAt = Date.now();
      var st = _comments[id] = _comments[id] || { open: true };
      st.list = (st.list || []).concat([{ name: (_who && _who.name) || 'You', store: null, body: text }]);
      renderFeed();
    } else {
      clearPin();
      if ((res.status === 401 || res.status === 429) && !isAccountMode()) askPin(function () { doAddComment(id, text); }, (d && d.error) || 'PIN check failed — try again');
      else if (typeof showToast !== 'undefined') showToast((d && d.error) || 'Could not comment', 'error');
    }
  } catch (e) {
    if (typeof showToast !== 'undefined') showToast('No connection — try again', 'error');
  }
}

// ── Confirm (per-person, PIN-attributed) ──────────────────────────────────────
function confirmPost(id) {
  if (isAccountMode()) return;
  // v125.51: identity on demand — reading never asked who you are, so ask now.
  if (!_who) { _pendingAction = function () { confirmPost(id); }; renderWhoPicker(); return; }
  if (pinFresh()) { doConfirm(id); return; }
  askPin(function () { doConfirm(id); });
}

async function doConfirm(id) {
  if (_confirming) return;
  _confirming = true;
  try {
    var res = await api('/api/news/confirm', {
      method: 'POST',
      body: JSON.stringify({ post_id: id, auth_staff_id: _who.id, auth_pin: _pin }),
    });
    var d = await res.json().catch(function () { return {}; });
    if (res.ok && d && d.ok) {
      _pinAt = Date.now();
      var p = _posts.find(function (x) { return x.id === id; });
      if (p) p.confirmed = true;
      updateNavBadge();
      renderFeed();
    } else {
      clearPin();
      if (res.status === 401 || res.status === 429) askPin(function () { doConfirm(id); }, (d && d.error) || 'PIN check failed — try again');
      else if (typeof showToast !== 'undefined') showToast((d && d.error) || 'Could not confirm', 'error');
    }
  } catch (e) {
    if (typeof showToast !== 'undefined') showToast('No connection — try again', 'error');
  } finally {
    _confirming = false;
  }
}

// The PIN prompt — memory only, 5-min idle expiry (chat A3 posture).
function askPin(then, errMsg) {
  var el = root(); if (!el) return;
  var old = document.getElementById('nwPinModal');
  if (old) old.remove();
  var wrap = document.createElement('div');
  wrap.id = 'nwPinModal';
  wrap.className = 'nw-modal';
  wrap.innerHTML = '<div class="nw-mcard">' +
    '<div style="font-weight:700;font-size:15px;color:var(--text)"><span translate="no">' + esc(_who ? _who.name : '') + '</span> — confirm it\'s you</div>' +
    '<div style="font-size:12.5px;color:var(--comms-sub);margin-top:6px">' + (errMsg ? esc(errMsg) : 'Enter your till PIN to confirm you\'ve read this.') + '</div>' +
    '<input id="nwPinInput" class="nw-mpin" type="password" inputmode="numeric" autocomplete="off" maxlength="8">' +
    '<button class="nw-mbtn" id="nwPinGo">Confirm</button>' +
    '<button class="nw-mcancel" id="nwPinCancel">Cancel</button></div>';
  el.appendChild(wrap);
  var input = document.getElementById('nwPinInput');
  input.focus();
  function go() {
    var v = String(input.value || '').trim();
    if (!v) return;
    _pin = v; _pinAt = Date.now();
    wrap.remove();
    then();
  }
  document.getElementById('nwPinGo').onclick = go;
  input.onkeydown = function (e) { if (e.key === 'Enter') go(); };
  document.getElementById('nwPinCancel').onclick = function () { wrap.remove(); };
}

// ── Background badge (light: every 5 min, visible page + identity only) ──────
if (typeof window !== 'undefined') {
  setInterval(function () {
    if (document.hidden) return;
    if (isAccountMode() || loadWho()) refreshFeed();
  }, 5 * 60 * 1000);
}

// ── Window bindings (classic-script bundle contract) ─────────────────────────
if (typeof window !== 'undefined') {
  window.renderNewsScreen = renderNewsScreen;
}

// v125.14 CRITICAL: the onclick handlers MUST be EXPORTED, not hung on a manual
// `window.News = {…}`. esbuild builds this bundle with `globalName:'News'`, which
// generates `var News = (IIFE)()` and assigns the module's EXPORTS to window.News
// — so an in-body `window.News = {…}` is overwritten with the exports moments
// later, and every `onclick="News.confirmPost()"` becomes a dead TypeError. The
// screen still renders (renderNewsScreen is a separate global) so it looked fine.
// v125.39 — jump to the store inbox (Comms -> Email). Guards are the classic-
// script contract: both globals exist at runtime; tests import this module bare.
function openStoreEmail() {
  try {
    if (typeof switchScreen !== 'undefined') switchScreen('chat');
    if (typeof window !== 'undefined' && window.Chat && window.Chat.commsTab) window.Chat.commsTab('email');
  } catch (e) { /* never break the news screen */ }
}

// Best-effort unread-email count for the notice-board card. Pre-go-live the
// API 503s and the card silently stays a signpost — no error state here.
async function fetchEmailPreview() {
  try {
    var q;
    if (isAccountMode()) {
      // scope an account's count to the store they're viewing (same as chat)
      var st = (typeof currentStore !== 'undefined' && currentStore) ? (currentStore.name || currentStore) : null;
      q = '/api/email/inbox' + (st ? '?store=' + encodeURIComponent(st) : '');
    } else {
      q = _who ? '/api/email/inbox?staff_id=' + encodeURIComponent(_who.id) : null;
    }
    if (!q) return;
    var res = await api(q);
    if (!res.ok) return;
    // The inbox API answered → email is LIVE. Only now does the card become
    // tappable (v125.50 — pre-go-live it's a plain signpost, never a trap).
    var card = document.getElementById('nwEmailCard');
    if (card) { card.setAttribute('onclick', 'News.openStoreEmail()'); card.style.cursor = 'pointer'; }
    var subEl = document.getElementById('nwEmailSub');
    if (subEl) subEl.textContent = 'Your store\'s inbox lives in Comms → 📧 Email. Tap to open it.';
    var d = await res.json();
    var unread = ((d && d.emails) || []).filter(function (m) { return m.direction === 'in' && !m.read_at; }).length;
    if (!unread) return;
    var b = document.getElementById('nwEmailBadge');
    var sub = document.getElementById('nwEmailSub');
    if (b) b.innerHTML = '<span style="font-size:11px;font-weight:700;color:#fff;background:#ff5c7a;padding:2px 8px;border-radius:10px;margin-inline-start:6px">' + unread + ' unread</span>';
    if (sub) sub.textContent = unread + (unread === 1 ? ' unread email in your store inbox — tap to read it.' : ' unread emails in your store inbox — tap to read them.');
  } catch (e) { /* signpost stays */ }
}
// Exporting the handlers puts them on window.News for real — the chat/dataCapture
// pattern. Locked by tests/comms-handlers-bound_test.js (loads the built bundle).
export {
  esc, initials, fmtWhen,
  pickWho, switchWho, cancelWhoPick, confirmPost, viewImage, toggleComments, addComment,
  openAttachment,                                    // v125.84 — document slot
  openStoreEmail,
  onSearch, setScope, setFilter,                     // v126.65 — search + All/Me filter (also driven by the Updates hub)
  setEmbedded, unreadCount,                          // v126.66 — Updates hub host + combined-badge count
};
